import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import React, { FC, useEffect, useState } from 'react';
import {
	Button,
	CloseIcon,
	CopyIcon, Dialog,
	Link,
	LoaderBars,
	UrlIcon,
	Tooltip,
	ButtonSize,
	ButtonColor,
} from '@aleph/shared-components';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import ExtensionInstructionsImg from 'wb-reddit/assets/images/extension-instructions.svg';
import { useMutation } from '@apollo/client';
import { CreateReportGoogleAccessDocument } from 'wb-reddit/graphql/generated';
import { Trans, useTranslation } from 'react-i18next';

interface RedditReportLinkingModalProps {
	report: RedditReport;
	onClose: () => void;
	isOpen: boolean;
}

const RedditReportLinkingModal: FC<RedditReportLinkingModalProps> = ({ report, onClose, isOpen }) => {
	const { t } = useTranslation();
	const [showTooltip, setShowTooltip] = useState<boolean>(false);
	const platformUser = useUserMetaData();

	const [createReportGoogleAccess, { loading }] = useMutation(CreateReportGoogleAccessDocument);

	useEffect(() => {
		if (report.reportApi?.reportApiToken && platformUser?.userId) {
			createReportGoogleAccess({
				variables: {
					reportApiToken: report.reportApi?.reportApiToken,
					userId:         platformUser?.userId,
				},
			});
		}
	}, []);

	const copyToClipboard = async () => {
		await navigator.clipboard.writeText(report.reportApi?.reportApiToken || 'Corrupted token');
		setShowTooltip(true);

		setTimeout(() => {
			setShowTooltip(false);
		}, 3000);
	};

	return (
		<Dialog
			onClose={onClose}
			isOpen={isOpen}
			className="max-h-[475px]"
		>
			<Dialog.Header preset={
				{
					prefix: <UrlIcon />,
					title:  t('re: reports.link', 'Link to live data source'),
					suffix: <Button
						leftNode={<CloseIcon />}
						appearance="ghost"
						color="gray"
						onClick={onClose}
						size={ButtonSize.LARGE}
					/>,
				}}
			/>
			<Dialog.Content className="font-medium text-[16px] text-gray-800">
				{t('re: reports.import_creds', 'Data import credentials')}
				<div className="text-[12px] font-normal py-[16px]">
					{t('re: reports.logged_in_as', { name: platformUser?.userEmail })}
					<Trans
						i18nKey="re: reports.logged_in_as"
						defaults="You are logged in as <strong>{{name}}</strong>"
						values={{ name: platformUser?.userEmail }}
					>
						You are logged in as <strong>{platformUser?.userEmail}</strong>
					</Trans>
				</div>
				<div
					className="flex items-center justify-between h-[64px] mb-[16px] px-[16px]
						rounded-[12px] bg-gray-100"
				>
					<div className="flex gap-[16px]">
						<div className="flex self-center text-[14px] text-gray-600 leading-[26px]">{t('re: reports.id', 'Report ID')}</div>
						<div>
							{
								loading ? <div className="flex h-full items-center gap-[8px] text-[12px]">
									<LoaderBars
										width={16}
										height={16}
										className="animate-spin"
									/>
									<strong>Loading</strong>
								</div> : <strong className="text-[12px] text-gray-950 font-medium">
									{report.reportApi?.reportApiToken || 'Corrupted token'}
								</strong>
							}
						</div>
					</div>
					{
						!loading
						&& <Tooltip
							content={t('sc: copied', 'Copied!')}
							trigger="click"
							isAlwaysVisible={showTooltip}
						>
							<Button
								appearance="ghost"
								onClick={copyToClipboard}
								leftNode={<CopyIcon stroke="stroke-current" />}
								color={ButtonColor.WISE_BLUE}
								title={t('sc: copy clipboard', 'Copy to clipboard')}
								className="flex gap-[8px] cursor-copy text-[12px]"
							/>
						</Tooltip>
					}
				</div>
				{/* TODO translate */}
				<div>Install ‘<strong className="font-bold">Wise.blue Sheets</strong>’ add-on</div>
				<div className="text-[12px] font-normal py-[16px]">
					To import data into Google Sheets please use the dedicated add-on. <Link href="https://google.com/"
						variant="external"
					>See instructions</Link>
				</div>
				<img src={ExtensionInstructionsImg}
					alt="extension-instructions"
				/>
			</Dialog.Content>
		</Dialog>
	);
};

export default RedditReportLinkingModal;
